<script setup>
import ArrowBottomIcon from "@/components/Icons/ArrowBottomIcon.vue";
import { t } from "@/controllerLK/AvalLocalization";
import {ref, defineProps, defineEmits, computed} from "vue";

const props = defineProps({
  items: {
    type: [Array, null],
    required: true,
    default: () => []
  },
  modelValue: {
    type: Array,
    default: () => []
  },
  type: {
    type: String,
    required: true
  }
});

// Локальное значение выбранных элементов.
const localModelValue = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    emit("update:modelValue", value);
  }
});

const emit = defineEmits(["toggle-item", "update:modelValue"]);


// Флаг открытия выпадаюещго списка
const isShowDropdown = ref(false);

// Вычисляемая функция получения лейбла
const getFilterLabel = computed(() => {
  if (props.type === "block") {
    return t("map_filter.theme_filter");
  } else {
    return t("map_filter.rubric_filter");
  }
});

// Вычисляемая функция получения отображаемого текста
const displayText = computed(() => {
  if (props.type === "block") {
    return themesDisplayText.value;
  } else {
    return rubricDisplayText.value;
  }
});

// Форматирование текста в поле выбора темы в зависимости от количества выбранных тем.
const themesDisplayText = computed(() => {

  const isAllThemesChosen = props.modelValue.length === props.items.length;

  if (isAllThemesChosen) {
    return t("news_page_themes_switcher.label.theme_all");
  }

  if (props.modelValue.length === 0) {
    return t("news_page_themes_switcher.label.theme_not_selected");
  }

  if (!isAllThemesChosen && props.modelValue.length === 1) {
    return props.modelValue[0].name;
  }

  if (!isAllThemesChosen && props.modelValue.length > 1) {
    return props.modelValue.length;
  }

  return "";
});

// Форматирование текста в поле выбора рубрики в зависимости от количества выбранных рубрик.
const rubricDisplayText = computed(() => {
  const isAllRubricsSelected = props.modelValue.length === props.items.length;

  if (props.modelValue.length === 0) {
    return t("news_page_rubrics_switcher.label.rubric_not_selected");
  }

  if (isAllRubricsSelected) {
    return t("news_page_rubrics_switcher.label.rubric_all");
  }

  if (!isAllRubricsSelected && props.modelValue.length === 1) {
    return props.modelValue[0].name;
  }

  if (!isAllRubricsSelected && props.modelValue.length > 1) {
    return props.modelValue.length;
  }

  return "";
});

</script>

<template>
  <Dropdown
    v-model="localModelValue"
    :items="props.items"
    label-key="name"
    teleport="#app"
    :use-cross-icon="false"
    :is-visible="isShowDropdown"
    max-width="220px"
    max-height="83%"
    @close="isShowDropdown = false"
  >
    <template #trigger>
      <div
        class="filter-switcher"
        :class="{'filter-switcher--active': isShowDropdown}"
        @click="isShowDropdown = !isShowDropdown"
      >
        <div class="filter-switcher__box">
          <span
            class="filter-switcher__box-title"
            :class="{'filter-switcher__box-title--active': isShowDropdown}"
          >
            {{ getFilterLabel }}
            <template v-if="typeof displayText === 'number'">
              ({{ displayText }})
            </template>
          </span>
          <span
            v-if="typeof displayText === 'string'"
            class="filter-switcher__box-name"
          >
            {{ displayText }}
          </span>
        </div>
        <ArrowBottomIcon
          class="filter-switcher__icon"
          :class="{'filter-switcher__icon--active': isShowDropdown}"
        />
      </div>
    </template>
  </Dropdown>
</template>

<style lang="scss" scoped>

.filter-switcher {
  background: var(--background-bg-white);
  display: flex;
  border-radius: 8px;
  justify-content: space-between;
  padding: 0 12px;
  cursor: pointer;
  height: 100%;
  align-items: center;
  gap: 4px;
  box-shadow: 1px 2px 10px 0 var(--gray-gray50);
  width: 219px;

  &--active {
    background: var(--background-bg-hover2);
  }

  &:hover {
    background: var(--background-bg-hover2);
  }

  &__icon {
    color: var(--icon-icon-secondary);

    &--active {
      rotate: 180deg;
      color: var(--icon-icon-on);
    }
  }

  &__box {
    display: flex;
    align-items: center;
    gap: 4px;

    &-title {
      color: var(--text-text-main);
      line-height: 18px;
      font-weight: 400;
      font-size: 14px;
      white-space: nowrap;

      &--active {
        color: var(--text-text-on);
      }
    }

    &-name {
      white-space: nowrap;
      max-width: 135px;
      line-clamp: 1;
      -webkit-line-clamp: 1;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
      color: var(--text-text-pur);
    }
  }
}
</style>